import React, { useState, useEffect } from 'react';
import { secureGet, securePost, securePut } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';

import plusIcon from '../../icons/plusD.svg';
import minusIcon from '../../icons/minusD.svg';

const TaskForm = ({
    title,
    projectId,
    technicians = [],
    initialValues = {},
    onSuccess,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({
        assignedToId: '',
        description: '',
        estimatedDuration: 0,
        productId: '',
        startDate: '',
        endDate: '',
    });
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    // Fetch services on component mount
    useEffect(() => {
        const fetchServices = async () => {
            setLoadingServices(true);
            try {
                const abortController = new AbortController();
                const response = await secureGet("/products/unpaginated?familyTypes=SERVICE", showSnackbar, { 
                    signal: abortController.signal 
                });
                const servicesData = response.data || [];
                setServices(servicesData.map((service) => ({
                    value: service.id,
                    label: service.name || service.reference || '',
                    name: service.name || service.reference || '',
                    reference: service.reference,
                })));
            } catch (error) {
                console.error("Failed to fetch services:", error);
            } finally {
                setLoadingServices(false);
            }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        if (initialValues && initialValues.id) {
            // Modify mode - populate form with initial values
            // Convert duration from hours (if it's already in hours) or assume it's in hours
            const duration = initialValues.estimatedDuration || 0;
            
            setFormValues({
                assignedToId: initialValues.assignedToId || '',
                description: initialValues.description || '',
                estimatedDuration: duration, // Duration is stored in hours
                productId: initialValues.productId || '',
                // Keep startDate and endDate from initialValues for payload consistency
                startDate: initialValues.startDate || '',
                endDate: initialValues.endDate || '',
            });
        } else {
            // Add mode - reset form (default to 0 hours)
            setFormValues({
                assignedToId: '',
                description: '',
                estimatedDuration: 0,
                productId: '',
                startDate: '',
                endDate: '',
            });
        }
    }, [initialValues]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleDurationChange = (delta) => {
        setFormValues((prev) => {
            // Increment/decrement by 0.1 hours (6 minutes)
            const increment = 0.1;
            const newDuration = prev.estimatedDuration + (delta * increment);
            // Round to 1 decimal place to avoid floating point precision issues
            const roundedDuration = Math.round(newDuration * 10) / 10;
            // Minimum is 0 (can be 0 for add mode)
            return {
                ...prev,
                estimatedDuration: Math.max(0, roundedDuration),
            };
        });
    };

    const formatDuration = (hours) => {
        // Format hours to display as "Xh" or "XhYm" format
        // hours is a decimal number (e.g., 1.5 = 1h30min)
        if (!hours || hours === 0) return '0h';
        
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        
        if (minutes === 0) {
            return `${wholeHours}h`;
        } else if (wholeHours === 0) {
            return `${minutes}min`;
        } else {
            return `${wholeHours}h${minutes}min`;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formValues.assignedToId) {
            showSnackbar('Veuillez sélectionner un technicien', 'error');
            return;
        }



        setLoading(true);

        try {
            const payload = {
                assignedToId: parseInt(formValues.assignedToId, 10),
                description: formValues.description,
                estimatedDuration: parseFloat(formValues.estimatedDuration.toFixed(1)), // Send as hours (decimal, e.g., 1.5)
            };

            // Include productId if selected
            if (formValues.productId) {
                payload.productId = parseInt(formValues.productId, 10);
            }

            // Include startDate and endDate if they exist (for modify mode to keep initialValues structure)
            if (formValues.startDate) {
                payload.startDate = formValues.startDate;
            }
            if (formValues.endDate) {
                payload.endDate = formValues.endDate;
            }

            if (initialValues?.id) {
                // Modify mode - PUT request
                payload.id = parseInt(initialValues.id, 10);
                await securePut(`/projects/tasks/${initialValues.id}`, payload, showSnackbar);
                onSuccess(t('confirmation.taskUpdated') || 'Tâche modifiée avec succès');
            } else {
                // Add mode - POST request
                console.log('Submitting payload:', payload);
                await securePost(`/projects/${projectId}/tasks/add`, payload, showSnackbar);
                onSuccess(t('confirmation.taskAdded') || 'Tâche ajoutée avec succès');
            }
        } catch (error) {
            console.error("Failed to submit task:", error);
        } finally {
            setLoading(false);
        }
    };

    const technicianOptions = technicians.map((tech) => ({
        value: tech.id,
        label: `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || tech.username || tech.name,
        name: `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || tech.username || tech.name,
    }));

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
            <div className="bg-[#F9FAFC] rounded-[12px] shadow-lg px-6 py-2 w-[90%] max-w-[700px] max-h-[90vh] flex flex-col">
                <Helmet>
                    <title>GrayFox - {title}</title>
                </Helmet>

                {/* Header */}
                <div className="p-6 pb-0">
                    <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                </div>

                {/* Content */}
                <div className="px-6 py-4 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex justify-center items-center h-[300px]">
                            <LoadingLogo />
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Services */}
                            <div>
                                <Input
                                    type="dropdown"
                                    label='Services'
                                    name="productId"
                                    value={formValues.productId}
                                    onChange={(value) => handleInputChange('productId', value)}
                                    options={services}
                                    disabled={loadingServices}
                                />
                            </div>

                            {/* Technician Assignment and Duration on same row */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Technician Assignment - Half width */}
                                <div>
                                    <Input
                                        type="dropdown"
                                        label={t('fields.assignedTo')}
                                        name="assignedToId"
                                        value={formValues.assignedToId}
                                        required
                                        onChange={(value) => handleInputChange('assignedToId', value)}
                                        options={technicianOptions}
                                    />
                                </div>

                                {/* Duration - Half width */}
                                <div className="flex items-center gap-3">
                                    <label className="text-[12px] font-semibold text-[#707070] uppercase whitespace-nowrap">
                                        {'DURÉE'}
                                    </label>
                                    <div className="flex items-stretch rounded-[12px] border border-[#E3E3E3] bg-[#ffffff] divide-x divide-[#E3E3E3] flex-1">
                                        <button
                                            type="button"
                                            onClick={() => handleDurationChange(-1)}
                                            className="w-10 h-10 flex items-center justify-center bg-white rounded-l-[12px] hover:bg-gray-100"
                                        >
                                            <img src={minusIcon} alt="Minus" className="w-4 h-4" />
                                        </button>
                                        <div className="flex items-center justify-center bg-white px-6 flex-1">
                                            <span className="text-[16px] font-bold text-[#38485C]">
                                                {formatDuration(formValues.estimatedDuration)}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleDurationChange(1)}
                                            className="w-10 h-10 flex items-center justify-center bg-white rounded-r-[12px] hover:bg-gray-100"
                                        >
                                            <img src={plusIcon} alt="Plus" className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="text-[12px] font-semibold text-[#707070] mb-2 block">
                                    {t('fields.description')}
                                </label>
                                <textarea
                                    name="description"
                                    value={formValues.description}
                                    optional={true}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    className="w-full min-h-[200px] px-4 py-3 border border-gray-300 rounded-[12px] text-[16px] font-semibold text-[#38485C] focus:outline-none focus:border-primary resize-y"
                                    placeholder={t('fields.description')}
                                />
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer with Save/Cancel */}
                <div className="p-6 pt-0 flex justify-end">
                    <SaveCancel
                        onCancelClick={onCancel}
                        onSaveClick={handleSubmit}
                        loading={loading}
                    />
                </div>
            </div>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default TaskForm;

